/*
 * Story 14.4 — Saved-look detail drawer web component.
 *
 * <saved-look-detail-drawer> wraps a native <dialog> and:
 *   - Subscribes to `benvel:wishlist-open-look` (dispatched by
 *     wishlist-look-card.liquid in Story 14.2) and renders the drawer
 *     with the look's items from `localStorage["benvel.wishlist.looks"]`.
 *   - Wires per-item Cambiar / Quitar handlers. Cambiar opens
 *     `<stylist-swap-panel>` (Epic 18) when
 *     `window.benvel.features?.stylistSwapPanel === true`, otherwise the
 *     in-drawer `bv-saved-look-swap-fallback-template` panel.
 *   - "Agregar look al carrito" composes a single POST /cart/add.js with
 *     the items array. On 200 dispatches a Horizon-native `cart:update`
 *     CustomEvent so <cart-drawer-component> refreshes via SRA and opens
 *     with the new line items. On 422 surfaces inline
 *     "Sin stock — quitar para continuar" on the offending row(s) and
 *     HOLDS the cart write (no partial-add). On 5xx surfaces an inline
 *     error in the footer error region.
 *   - "Compartir por WhatsApp" dispatches `benvel:wishlist-share-open`
 *     with `{ lookId, intent: 'amiga' }`. Story 14.3's
 *     wishlist-share-button (mounted in the wishlist toolbar) renders
 *     the actual share UI via the share dialog.
 *
 * All `benvel:saved-look-*` events are CONSENT-GATED via
 * `window.benvel.consent.has('analytics')` (Story 11.4a + Habeas Data).
 *
 * No external deps. Vanilla ES2022. Idempotent: re-registration of the
 * custom element is guarded; multiple `<saved-look-detail-drawer>`
 * instances on a page are unusual but supported.
 *
 * Persistence schema (`localStorage["benvel.wishlist.looks"]`,
 * authored by Story 14.1's save-look-CTA flow):
 *   [{
 *     lookId: string,
 *     title: string,
 *     occasion: string,
 *     savedAt: ISO-8601 string,
 *     totalPrice: number (COP),
 *     productRefs: [{ image, title, productId, variantId, handle,
 *                     productUrl, price, color, size }, ...],
 *     selectedVariants: [{ variantId, price, ... }, ...],
 *     scene: { src, srcset, sizes },
 *     alternatives: [ Array<altItem> | null ]  (one slot per item;
 *       optional pre-seed by Epic 18; v1 looks usually have this empty)
 *   }, ...]
 *
 * The persistence-schema is intentionally tolerant: missing fields
 * render the row gracefully (no thumbnail / no price / etc.) so v1
 * looks saved by partial Epic 18 / Epic 16 flows still open without
 * a JS error.
 */

(function () {
  "use strict";

  if (window.customElements && window.customElements.get("saved-look-detail-drawer")) {
    return;
  }

  var LOOKS_KEY = "benvel.wishlist.looks";

  // ──────────────────────────────────────────────────────────────────
  // Storage helpers
  // ──────────────────────────────────────────────────────────────────

  function readLooks() {
    try {
      var raw = window.localStorage.getItem(LOOKS_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_e) {
      return [];
    }
  }

  function writeLooks(list) {
    try {
      window.localStorage.setItem(LOOKS_KEY, JSON.stringify(list));
      return true;
    } catch (_e) {
      return false;
    }
  }

  function findLookIndex(list, lookId) {
    if (!lookId) return -1;
    for (var i = 0; i < list.length; i += 1) {
      if (list[i] && String(list[i].lookId) === String(lookId)) return i;
    }
    return -1;
  }

  // ──────────────────────────────────────────────────────────────────
  // Currency formatter
  // ──────────────────────────────────────────────────────────────────

  function formatCOP(value) {
    if (typeof value !== "number" || isNaN(value)) value = 0;
    try {
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        maximumFractionDigits: 0,
      }).format(value);
    } catch (_e) {
      return "$" + Math.round(value).toLocaleString("es-CO");
    }
  }

  function formatSavedAt(iso) {
    if (!iso) return "";
    try {
      var d = new Date(iso);
      if (isNaN(d.getTime())) return "";
      return new Intl.DateTimeFormat("es-CO", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(d);
    } catch (_e) {
      return "";
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // Consent-gated analytics
  // ──────────────────────────────────────────────────────────────────

  function emitAnalytics(eventName, detail) {
    var consent =
      typeof window !== "undefined" && window.benvel ? window.benvel.consent : null;
    if (!consent || typeof consent.has !== "function") return;
    if (consent.has("analytics") !== true) return;
    document.dispatchEvent(
      new CustomEvent(eventName, { bubbles: true, detail: detail || {} })
    );
  }

  // ──────────────────────────────────────────────────────────────────
  // Item normalizer — tolerant of partial v1 look schemas
  // ──────────────────────────────────────────────────────────────────

  function normalizeItems(look) {
    if (!look) return [];
    // Prefer an explicit `items` array if Story 14.1 evolves the schema.
    if (Array.isArray(look.items) && look.items.length > 0) {
      return look.items.map(normalizeItem);
    }
    // Compose from productRefs + selectedVariants (v1 schema).
    var refs = Array.isArray(look.productRefs) ? look.productRefs : [];
    var variants = Array.isArray(look.selectedVariants) ? look.selectedVariants : [];
    return refs.map(function (ref, i) {
      var variant = variants[i] || {};
      return normalizeItem({
        title: ref.title,
        image: ref.image,
        productId: ref.productId || variant.productId,
        variantId: ref.variantId || variant.variantId,
        handle: ref.handle,
        productUrl: ref.productUrl,
        price: typeof ref.price === "number" ? ref.price : variant.price,
        color: ref.color || variant.color,
        size: ref.size || variant.size,
      });
    });
  }

  function normalizeItem(raw) {
    raw = raw || {};
    return {
      title: raw.title || "",
      image: raw.image || "",
      productId: raw.productId == null ? null : String(raw.productId),
      variantId: raw.variantId == null ? null : String(raw.variantId),
      handle: raw.handle || null,
      productUrl: raw.productUrl || (raw.handle ? "/products/" + raw.handle : "#"),
      price: typeof raw.price === "number" ? raw.price : 0,
      color: raw.color || "",
      size: raw.size || "",
      availability: "available",
    };
  }

  function computeTotal(items) {
    var total = 0;
    for (var i = 0; i < items.length; i += 1) {
      var price = items[i] && typeof items[i].price === "number" ? items[i].price : 0;
      total += price;
    }
    return total;
  }

  function interp(template, replacements) {
    if (!template) return "";
    return Object.keys(replacements).reduce(function (out, key) {
      return out.replace(key, replacements[key]);
    }, template);
  }

  // ──────────────────────────────────────────────────────────────────
  // Web component
  // ──────────────────────────────────────────────────────────────────

  var SavedLookDetailDrawer = (function () {
    function SavedLookDetailDrawer() {
      var instance = Reflect.construct(HTMLElement, [], SavedLookDetailDrawer);
      return instance;
    }
    SavedLookDetailDrawer.prototype = Object.create(HTMLElement.prototype);
    SavedLookDetailDrawer.prototype.constructor = SavedLookDetailDrawer;

    SavedLookDetailDrawer.prototype.connectedCallback = function () {
      this._currentLookId = null;
      this._currentItems = [];
      this._currentLook = null;
      this._lastTriggerEl = null;

      this._dialog = this.querySelector("[data-bv-saved-look-dialog]");
      this._titleEl = this.querySelector("[data-bv-saved-look-title]");
      this._piecesEl = this.querySelector("[data-bv-saved-look-pieces]");
      this._sublineEl = this.querySelector("[data-bv-saved-look-subline]");
      this._occasionEl = this.querySelector("[data-bv-saved-look-occasion]");
      this._heroImg = this.querySelector("[data-bv-saved-look-hero-img]");
      this._itemsEl = this.querySelector("[data-bv-saved-look-items]");
      this._totalEl = this.querySelector("[data-bv-saved-look-total]");
      this._errorEl = this.querySelector("[data-bv-saved-look-error]");
      this._bodyEl = this.querySelector("[data-bv-saved-look-body]");
      this._swapHost = this.querySelector("[data-bv-saved-look-swap-host]");
      this._addBtn = this.querySelector("[data-bv-saved-look-add]");
      this._shareBtn = this.querySelector("[data-bv-saved-look-share]");
      this._closeBtn = this.querySelector("[data-bv-saved-look-close]");

      this._lineTemplate = document.getElementById("bv-saved-look-line-item-template");
      this._swapTemplate = document.getElementById("bv-saved-look-swap-fallback-template");
      this._altTemplate = document.getElementById("bv-saved-look-swap-alt-template");

      // i18n templates (from data-* attrs on host element).
      this._localeAddAll = this.getAttribute("data-locale-add-all") || "";
      this._localeAdding = this.getAttribute("data-locale-adding") || "";
      this._localeSizeLabel = this.getAttribute("data-locale-size-label") || "";
      this._localeColorLabel = this.getAttribute("data-locale-color-label") || "";
      this._localePiecesSaved = this.getAttribute("data-locale-pieces-saved") || "";
      this._localeSubline = this.getAttribute("data-locale-subline-saved-at") || "";
      this._localeAriaRemove = this.getAttribute("data-locale-aria-remove-item") || "";
      this._localeAriaSwap = this.getAttribute("data-locale-aria-swap-item") || "";
      this._localeErrorAdd = this.getAttribute("data-locale-error-add-failed") || "";
      this._localeErrorSome = this.getAttribute("data-locale-error-some-unavailable") || "";

      // Bindings
      this._onOpenLook = this._onOpenLook.bind(this);
      this._onClose = this._onClose.bind(this);
      this._onAddAll = this._onAddAll.bind(this);
      this._onShare = this._onShare.bind(this);
      this._onDialogCancel = this._onDialogCancel.bind(this);
      this._onDialogClose = this._onDialogClose.bind(this);
      this._onBackdropClick = this._onBackdropClick.bind(this);
      this._onItemsClick = this._onItemsClick.bind(this);
      this._onSwapHostClick = this._onSwapHostClick.bind(this);

      document.addEventListener("benvel:wishlist-open-look", this._onOpenLook);
      if (this._closeBtn) this._closeBtn.addEventListener("click", this._onClose);
      if (this._addBtn) this._addBtn.addEventListener("click", this._onAddAll);
      if (this._shareBtn) this._shareBtn.addEventListener("click", this._onShare);
      if (this._dialog) {
        this._dialog.addEventListener("cancel", this._onDialogCancel);
        this._dialog.addEventListener("close", this._onDialogClose);
        this._dialog.addEventListener("click", this._onBackdropClick);
      }
      if (this._itemsEl) this._itemsEl.addEventListener("click", this._onItemsClick);
      if (this._swapHost) this._swapHost.addEventListener("click", this._onSwapHostClick);
    };

    SavedLookDetailDrawer.prototype.disconnectedCallback = function () {
      document.removeEventListener("benvel:wishlist-open-look", this._onOpenLook);
    };

    // ──────────────────────────────────────────────────────────────
    // Open / close
    // ──────────────────────────────────────────────────────────────

    SavedLookDetailDrawer.prototype._onOpenLook = function (event) {
      var lookId = event && event.detail && event.detail.lookId;
      if (!lookId) return;
      var looks = readLooks();
      var idx = findLookIndex(looks, lookId);
      if (idx === -1) return;
      var look = looks[idx];
      this._currentLookId = String(lookId);
      this._currentLook = look;
      this._currentItems = normalizeItems(look);
      this._lastTriggerEl =
        document.activeElement && document.activeElement !== document.body
          ? document.activeElement
          : null;
      this._render();
      this._setMode("list");
      this._clearError();
      this._showDialog();
      emitAnalytics("benvel:saved-look-opened", {
        lookId: this._currentLookId,
        occasion: look.occasion || null,
        itemCount: this._currentItems.length,
      });
    };

    SavedLookDetailDrawer.prototype._showDialog = function () {
      if (!this._dialog) return;
      if (typeof this._dialog.showModal === "function" && !this._dialog.open) {
        try {
          this._dialog.showModal();
        } catch (_e) {
          // Already open or HTML detached — silent fallback.
          this._dialog.setAttribute("open", "");
        }
      } else {
        this._dialog.setAttribute("open", "");
      }
      // Move focus to close button (first focusable, dialog autofocus
      // convention). Defer to next frame so the slide-in is visible.
      var self = this;
      requestAnimationFrame(function () {
        if (self._closeBtn) self._closeBtn.focus({ preventScroll: true });
      });
    };

    SavedLookDetailDrawer.prototype._onClose = function () {
      this._closeDialog();
    };

    SavedLookDetailDrawer.prototype._onDialogCancel = function () {
      // Native ESC press fires cancel — `close` event follows immediately.
    };

    SavedLookDetailDrawer.prototype._onDialogClose = function () {
      // Restore focus to the originating trigger.
      var trigger = this._lastTriggerEl;
      this._lastTriggerEl = null;
      if (trigger && typeof trigger.focus === "function") {
        try {
          trigger.focus({ preventScroll: false });
        } catch (_e) {
          /* trigger detached — accept default focus. */
        }
      }
    };

    SavedLookDetailDrawer.prototype._onBackdropClick = function (event) {
      // <dialog> renders the backdrop as part of the dialog element; clicks
      // outside the panel hit the dialog itself.
      if (event.target === this._dialog) {
        this._closeDialog();
      }
    };

    SavedLookDetailDrawer.prototype._closeDialog = function () {
      if (!this._dialog) return;
      if (this._dialog.open) {
        try {
          this._dialog.close();
        } catch (_e) {
          this._dialog.removeAttribute("open");
        }
      } else {
        this._dialog.removeAttribute("open");
      }
    };

    // ──────────────────────────────────────────────────────────────
    // Render
    // ──────────────────────────────────────────────────────────────

    SavedLookDetailDrawer.prototype._render = function () {
      var look = this._currentLook || {};
      var items = this._currentItems;

      if (this._titleEl) this._titleEl.textContent = look.title || "";
      if (this._occasionEl) this._occasionEl.textContent = look.occasion || "";
      if (this._piecesEl) {
        this._piecesEl.textContent = interp(this._localePiecesSaved, {
          "__COUNT__": String(items.length),
        });
      }
      if (this._sublineEl) {
        var savedAt = formatSavedAt(look.savedAt);
        this._sublineEl.textContent = savedAt
          ? interp(this._localeSubline, { "__DATE__": savedAt })
          : "";
      }
      if (this._heroImg) {
        var heroSrc =
          (look.scene && look.scene.src) ||
          (look.productRefs && look.productRefs[0] && look.productRefs[0].image) ||
          (items[0] && items[0].image) ||
          "";
        if (heroSrc) {
          this._heroImg.src = heroSrc;
          this._heroImg.alt = look.title || "";
          this._heroImg.style.display = "";
        } else {
          this._heroImg.removeAttribute("src");
          this._heroImg.style.display = "none";
        }
      }
      this._renderItems();
      this._renderTotal();
    };

    SavedLookDetailDrawer.prototype._renderItems = function () {
      if (!this._itemsEl || !this._lineTemplate) return;
      this._itemsEl.innerHTML = "";
      var self = this;
      this._currentItems.forEach(function (item, index) {
        var node = self._lineTemplate.content.firstElementChild.cloneNode(true);
        node.setAttribute("data-item-index", String(index));
        if (item.productId) node.setAttribute("data-product-id", item.productId);
        if (item.variantId) node.setAttribute("data-variant-id", item.variantId);
        if (item.handle) node.setAttribute("data-handle", item.handle);
        if (item.productUrl) node.setAttribute("data-url", item.productUrl);
        node.setAttribute("data-price", String(item.price || 0));
        node.setAttribute("data-availability", item.availability || "available");

        var titleEl = node.querySelector("[data-bv-item-title]");
        if (titleEl) {
          titleEl.textContent = item.title || "";
          titleEl.href = item.productUrl || "#";
        }
        var linkEl = node.querySelector("[data-bv-item-link]");
        if (linkEl) linkEl.href = item.productUrl || "#";

        var thumbEl = node.querySelector("[data-bv-item-thumb]");
        if (thumbEl) {
          if (item.image) {
            thumbEl.src = item.image;
            thumbEl.alt = item.title || "";
          } else {
            thumbEl.removeAttribute("src");
            thumbEl.alt = "";
          }
        }

        var sizeEl = node.querySelector("[data-bv-item-size]");
        if (sizeEl) {
          sizeEl.textContent = item.size
            ? interp(self._localeSizeLabel, { "__SIZE__": item.size })
            : "";
        }
        var colorEl = node.querySelector("[data-bv-item-color]");
        if (colorEl) {
          colorEl.textContent = item.color
            ? interp(self._localeColorLabel, { "__COLOR__": item.color })
            : "";
        }

        var priceEl = node.querySelector("[data-bv-item-price]");
        if (priceEl) priceEl.textContent = formatCOP(item.price);

        var availabilityEl = node.querySelector("[data-bv-item-availability]");
        if (availabilityEl) {
          if (item.availability === "sold_out") {
            availabilityEl.removeAttribute("hidden");
          } else {
            availabilityEl.setAttribute("hidden", "");
          }
        }

        var swapBtn = node.querySelector("[data-bv-item-swap]");
        if (swapBtn) {
          swapBtn.setAttribute(
            "aria-label",
            interp(self._localeAriaSwap, { "__TITLE__": item.title || "" })
          );
        }
        var removeBtn = node.querySelector("[data-bv-item-remove]");
        if (removeBtn) {
          removeBtn.setAttribute(
            "aria-label",
            interp(self._localeAriaRemove, { "__TITLE__": item.title || "" })
          );
        }

        self._itemsEl.appendChild(node);
      });
    };

    SavedLookDetailDrawer.prototype._renderTotal = function () {
      if (!this._totalEl) return;
      this._totalEl.textContent = formatCOP(computeTotal(this._currentItems));
    };

    SavedLookDetailDrawer.prototype._setMode = function (mode) {
      if (!this._bodyEl) return;
      this._bodyEl.setAttribute("data-bv-saved-look-mode", mode);
    };

    SavedLookDetailDrawer.prototype._clearError = function () {
      if (!this._errorEl) return;
      this._errorEl.textContent = "";
      this._errorEl.setAttribute("hidden", "");
    };

    SavedLookDetailDrawer.prototype._setError = function (msg) {
      if (!this._errorEl) return;
      this._errorEl.textContent = msg || "";
      if (msg) {
        this._errorEl.removeAttribute("hidden");
      } else {
        this._errorEl.setAttribute("hidden", "");
      }
    };

    // ──────────────────────────────────────────────────────────────
    // Per-item event handlers
    // ──────────────────────────────────────────────────────────────

    SavedLookDetailDrawer.prototype._onItemsClick = function (event) {
      var target = event.target;
      if (!target) return;
      var row = target.closest("[data-item-index]");
      if (!row) return;
      var index = parseInt(row.getAttribute("data-item-index"), 10);
      if (isNaN(index)) return;

      if (target.closest("[data-bv-item-remove]")) {
        event.preventDefault();
        this._removeItem(index);
        return;
      }
      if (target.closest("[data-bv-item-swap]")) {
        event.preventDefault();
        this._openSwapFlow(index);
        return;
      }
      // Otherwise: title or media link → let the anchor navigate normally.
    };

    SavedLookDetailDrawer.prototype._removeItem = function (index) {
      var item = this._currentItems[index];
      if (!item) return;
      var lookId = this._currentLookId;
      this._currentItems.splice(index, 1);

      // Persist back to localStorage.
      var looks = readLooks();
      var lookIdx = findLookIndex(looks, lookId);
      if (lookIdx !== -1) {
        // Re-derive arrays in step with the normalized items.
        var look = looks[lookIdx];
        if (Array.isArray(look.items)) {
          look.items.splice(index, 1);
        }
        if (Array.isArray(look.productRefs)) {
          look.productRefs.splice(index, 1);
        }
        if (Array.isArray(look.selectedVariants)) {
          look.selectedVariants.splice(index, 1);
        }
        if (Array.isArray(look.alternatives)) {
          look.alternatives.splice(index, 1);
        }
        // Recompute totalPrice.
        look.totalPrice = computeTotal(this._currentItems);
        looks[lookIdx] = look;
        writeLooks(looks);
      }

      emitAnalytics("benvel:saved-look-item-removed", {
        lookId: lookId,
        productId: item.productId,
        variantId: item.variantId,
        itemIndex: index,
      });

      // Decision §D — auto-close + auto-remove + return focus when empty.
      if (this._currentItems.length === 0) {
        this._handleLookEmptied(lookId);
        return;
      }

      this._renderItems();
      this._renderTotal();
      this._clearError();

      // Notify the wishlist page so the grid re-hydrates the look's
      // thumb-strip / count.
      document.dispatchEvent(
        new CustomEvent("benvel:wishlist-removed", { bubbles: true, detail: { lookId: lookId } })
      );
    };

    SavedLookDetailDrawer.prototype._handleLookEmptied = function (lookId) {
      // Remove the entire look from localStorage and close drawer.
      var looks = readLooks();
      var idx = findLookIndex(looks, lookId);
      if (idx !== -1) {
        looks.splice(idx, 1);
        writeLooks(looks);
      }
      emitAnalytics("benvel:saved-look-emptied", { lookId: lookId });
      document.dispatchEvent(
        new CustomEvent("benvel:wishlist-removed", { bubbles: true, detail: { lookId: lookId } })
      );
      this._closeDialog();
    };

    // ──────────────────────────────────────────────────────────────
    // Swap fallback
    // ──────────────────────────────────────────────────────────────

    SavedLookDetailDrawer.prototype._openSwapFlow = function (itemIndex) {
      var item = this._currentItems[itemIndex];
      if (!item) return;
      var features = (window.benvel && window.benvel.features) || {};
      if (features.stylistSwapPanel === true) {
        // Epic 18 path — fire-and-forget.
        document.dispatchEvent(
          new CustomEvent("benvel:stylist-swap-open", {
            bubbles: true,
            detail: {
              lookId: this._currentLookId,
              itemIndex: itemIndex,
              productId: item.productId,
              occasion: this._currentLook && this._currentLook.occasion,
            },
          })
        );
        return;
      }
      // Pre-Epic-18 path — mount the in-drawer fallback panel.
      this._renderSwapFallback(itemIndex);
      this._setMode("swap-fallback");
    };

    SavedLookDetailDrawer.prototype._renderSwapFallback = function (itemIndex) {
      if (!this._swapHost || !this._swapTemplate) return;
      this._swapHost.innerHTML = "";
      var node = this._swapTemplate.content.firstElementChild.cloneNode(true);
      node.setAttribute("data-item-index", String(itemIndex));

      var look = this._currentLook || {};
      var altsForItem =
        Array.isArray(look.alternatives) && Array.isArray(look.alternatives[itemIndex])
          ? look.alternatives[itemIndex]
          : [];
      var bodyEl = node.querySelector(".bv-saved-look-swap-fallback__body");
      var listEl = node.querySelector("[data-bv-swap-fallback-list]");
      var viewOriginalEl = node.querySelector("[data-bv-swap-fallback-view-original]");

      var item = this._currentItems[itemIndex];
      if (viewOriginalEl && item) {
        viewOriginalEl.href = item.productUrl || "#";
      }

      if (altsForItem.length > 0 && this._altTemplate) {
        if (bodyEl) bodyEl.setAttribute("data-bv-swap-fallback-mode", "list");
        altsForItem.slice(0, 3).forEach(function (alt) {
          var altNode = this._altTemplate.content.firstElementChild.cloneNode(true);
          var altLink = altNode.querySelector("[data-bv-alt-link]");
          var altThumb = altNode.querySelector("[data-bv-alt-thumb]");
          var altTitle = altNode.querySelector("[data-bv-alt-title]");
          var altPrice = altNode.querySelector("[data-bv-alt-price]");
          if (altLink) altLink.href = alt.productUrl || (alt.handle ? "/products/" + alt.handle : "#");
          if (altThumb && alt.image) {
            altThumb.src = alt.image;
            altThumb.alt = alt.title || "";
          }
          if (altTitle) altTitle.textContent = alt.title || "";
          if (altPrice) altPrice.textContent = formatCOP(alt.price);
          if (listEl) listEl.appendChild(altNode);
        }, this);
      } else if (bodyEl) {
        bodyEl.setAttribute("data-bv-swap-fallback-mode", "empty");
      }

      this._swapHost.appendChild(node);
    };

    SavedLookDetailDrawer.prototype._onSwapHostClick = function (event) {
      var target = event.target;
      if (!target) return;

      if (target.closest("[data-bv-swap-fallback-close]")) {
        event.preventDefault();
        this._setMode("list");
        return;
      }
      if (target.closest("[data-bv-swap-fallback-remove]")) {
        event.preventDefault();
        var panel = target.closest("[data-bv-swap-fallback]");
        if (panel) {
          var idx = parseInt(panel.getAttribute("data-item-index"), 10);
          if (!isNaN(idx)) {
            this._setMode("list");
            this._removeItem(idx);
          }
        }
        return;
      }
      // [data-bv-swap-fallback-view-original] is an <a> — let the browser navigate.
    };

    // ──────────────────────────────────────────────────────────────
    // Add-all to cart
    // ──────────────────────────────────────────────────────────────

    SavedLookDetailDrawer.prototype._onAddAll = function () {
      var self = this;
      if (!this._currentItems || this._currentItems.length === 0) return;
      var failingPriorVariants = this._currentItems
        .filter(function (it) {
          return it.availability === "sold_out";
        })
        .map(function (it) {
          return it.variantId;
        });
      if (failingPriorVariants.length > 0) {
        this._setError(this._localeErrorSome);
        return;
      }

      var items = this._currentItems
        .filter(function (it) {
          return it.variantId != null;
        })
        .map(function (it) {
          return { id: it.variantId, quantity: 1 };
        });
      if (items.length === 0) {
        this._setError(this._localeErrorAdd);
        return;
      }

      this._clearError();
      var originalLabel = this._addBtn ? this._addBtn.textContent : "";
      if (this._addBtn) {
        this._addBtn.disabled = true;
        this._addBtn.textContent = this._localeAdding || originalLabel;
      }

      var fetchOpts = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({ items: items }),
      };
      window
        .fetch("/cart/add.js", fetchOpts)
        .then(function (res) {
          return res.json().then(function (payload) {
            return { ok: res.ok, status: res.status, payload: payload };
          });
        })
        .then(function (result) {
          if (result.ok) {
            return self._handleAddSuccess(result.payload, items);
          }
          if (result.status === 422) {
            return self._handleAddSoldOut(result.payload, items);
          }
          self._handleAddError();
        })
        .catch(function () {
          self._handleAddError();
        })
        .finally(function () {
          if (self._addBtn) {
            self._addBtn.disabled = false;
            self._addBtn.textContent = originalLabel;
          }
        });
    };

    SavedLookDetailDrawer.prototype._handleAddSuccess = function (payload, items) {
      var self = this;
      var lookId = this._currentLookId;
      var total = computeTotal(this._currentItems);

      // Fire Horizon-native `cart:update` event so <cart-drawer-component>
      // refreshes via SRA and opens the cart drawer. Mimic the
      // CartAddEvent shape (events.js) without importing the class.
      document.dispatchEvent(
        new CustomEvent("cart:update", {
          bubbles: true,
          detail: {
            resource: payload,
            sourceId: "saved-look-detail-drawer",
            data: {
              source: "saved-look-detail-drawer",
              itemCount: payload && payload.item_count,
              variantId: items[0] && items[0].id,
              didError: false,
            },
          },
        })
      );

      // Consent-gated analytics.
      emitAnalytics("benvel:saved-look-added-to-cart", {
        lookId: lookId,
        items: items.map(function (it) {
          return { variantId: it.id, quantity: it.quantity };
        }),
        total: total,
      });

      // Close the drawer — the cart-drawer takes over the UI surface.
      this._closeDialog();
    };

    SavedLookDetailDrawer.prototype._handleAddSoldOut = function (payload, items) {
      var self = this;
      var description =
        (payload && (payload.description || payload.message)) || "";
      var failedVariants = [];
      // Best-effort heuristic: variants whose `/products/{handle}.js`
      // refresh shows them unavailable. The 422 description is human
      // copy, so we re-validate by fetching each handle.
      var handlePromises = this._currentItems.map(function (it) {
        if (!it.handle || !it.variantId) return Promise.resolve(null);
        return window
          .fetch("/products/" + it.handle + ".js", { headers: { Accept: "application/json" } })
          .then(function (res) {
            if (!res.ok) return null;
            return res.json();
          })
          .then(function (product) {
            if (!product || !Array.isArray(product.variants)) return null;
            var v = product.variants.find(function (variant) {
              return String(variant.id) === String(it.variantId);
            });
            if (!v) return null;
            return { variantId: String(it.variantId), available: !!v.available };
          })
          .catch(function () {
            return null;
          });
      });

      Promise.all(handlePromises).then(function (results) {
        var firstOffenderEl = null;
        results.forEach(function (result, idx) {
          if (!result) return;
          if (!result.available) {
            failedVariants.push(result.variantId);
            self._currentItems[idx].availability = "sold_out";
            var row = self._itemsEl
              ? self._itemsEl.querySelector(
                  '[data-item-index="' + idx + '"]'
                )
              : null;
            if (row) {
              row.setAttribute("data-availability", "sold_out");
              var availEl = row.querySelector("[data-bv-item-availability]");
              if (availEl) availEl.removeAttribute("hidden");
              if (!firstOffenderEl) firstOffenderEl = row;
            }
          }
        });

        if (failedVariants.length === 0) {
          // 422 without a resolvable sold-out variant — fall back to
          // generic error.
          self._setError(self._localeErrorAdd);
          return;
        }

        self._setError(self._localeErrorSome);
        if (firstOffenderEl && typeof firstOffenderEl.focus === "function") {
          firstOffenderEl.setAttribute("tabindex", "-1");
          try {
            firstOffenderEl.focus({ preventScroll: false });
          } catch (_e) {
            /* swallow */
          }
        }

        emitAnalytics("benvel:saved-look-add-failed", {
          lookId: self._currentLookId,
          failedVariantIds: failedVariants,
          description: description,
        });
      });
    };

    SavedLookDetailDrawer.prototype._handleAddError = function () {
      this._setError(this._localeErrorAdd);
      emitAnalytics("benvel:saved-look-add-failed", {
        lookId: this._currentLookId,
        failedVariantIds: [],
      });
    };

    // ──────────────────────────────────────────────────────────────
    // Share via WhatsApp
    // ──────────────────────────────────────────────────────────────

    SavedLookDetailDrawer.prototype._onShare = function () {
      if (!this._currentLookId) return;
      document.dispatchEvent(
        new CustomEvent("benvel:wishlist-share-open", {
          bubbles: true,
          detail: { lookId: this._currentLookId, intent: "amiga" },
        })
      );
    };

    return SavedLookDetailDrawer;
  })();

  if (typeof window !== "undefined" && window.customElements) {
    if (!window.customElements.get("saved-look-detail-drawer")) {
      window.customElements.define("saved-look-detail-drawer", SavedLookDetailDrawer);
    }
  }
})();
